<template>
  <div v-if="visible" class="dds-regions-info mb-5">
    <!-- Red close button — top-right corner -->
    <v-btn
      fab
      x-small
      color="red darken-1"
      class="close-btn elevation-2"
      @click="dismiss"
    >
      <v-icon small color="white">mdi-close</v-icon>
    </v-btn>

    <p class="font-weight-semibold text-subtitle-1 mb-1 primary--text">
      {{ $t('supportedRegions.title') }}
    </p>
    <p class="text-body-2 mb-3 grey--text text--darken-2">
      {{ $t('supportedRegions.description') }}
    </p>

    <!-- Grid wrapper: 2 rows visible by default, overflow hidden -->
    <div
      class="grid-wrapper"
      :style="{ maxHeight: showAll ? '1000px' : collapsedHeight }"
    >
      <div class="countries-grid">
        <div
          v-for="country in DDS_SUPPORTED_COUNTRIES"
          :key="country.code"
          class="country-card"
        >
          <!-- v-menu renders the states popup without affecting grid layout -->
          <v-menu
            v-if="country.states.length"
            v-model="expanded[country.code]"
            :close-on-content-click="false"
            :nudge-bottom="4"
            offset-y
            max-width="300"
            min-width="220"
          >
            <template v-slot:activator="{ on }">
              <div class="country-header" v-on="on">
                <span class="country-flag mr-1">{{ country.flag }}</span>
                <span class="country-name">{{ country.name }}</span>
                <v-chip x-small color="primary" class="ml-1 count-chip">
                  {{ country.states.length }}
                </v-chip>
                <v-icon x-small class="ml-auto expand-icon" color="primary">
                  {{ expanded[country.code] ? 'mdi-chevron-up' : 'mdi-chevron-down' }}
                </v-icon>
              </div>
            </template>

            <v-card class="states-popup pa-2">
              <p class="text-caption font-weight-semibold primary--text mb-2 px-1">
                {{ country.flag }} {{ country.name }}
              </p>
              <div class="states-chips">
                <v-chip
                  v-for="state in country.states"
                  :key="state.code"
                  x-small
                  outlined
                  color="primary"
                  class="mr-1 mb-1"
                >
                  {{ state.name }}
                </v-chip>
              </div>
            </v-card>
          </v-menu>

          <!-- Country without sub-regions: no menu, just display -->
          <div v-else class="country-header no-pointer">
            <span class="country-flag mr-1">{{ country.flag }}</span>
            <span class="country-name">{{ country.name }}</span>
          </div>
        </div>
      </div>
    </div>

    <!-- Footer row: footer text left, expand/collapse button right -->
    <div class="d-flex align-center mt-2">
      <p class="text-caption mb-0 grey--text text--darken-1 flex-grow-1">
        {{ $t('supportedRegions.footer') }}
      </p>
      <v-btn
        small
        depressed
        color="primary"
        class="ml-2 flex-shrink-0 white--text show-all-btn"
        @click="showAll = !showAll"
      >
        <v-icon small class="mr-1">
          {{ showAll ? 'mdi-chevron-up' : 'mdi-earth' }}
        </v-icon>
        <strong>{{ showAll ? $t('supportedRegions.showLess') : $t('supportedRegions.showAll', { count: DDS_SUPPORTED_COUNTRIES.length }) }}</strong>
      </v-btn>
    </div>
  </div>
</template>

<script>
import { DDS_SUPPORTED_COUNTRIES } from '@/constants/ddsSupportedRegions';

const LS_KEY = 'dds_supported_regions_info_dismissed';
// Each country card row height (px) + gap
const ROW_HEIGHT = 34;
const ROW_GAP    = 4;
const VISIBLE_ROWS = 2;

export default {
  name: 'DdsSupportedRegionsInfo',

  data() {
    const expanded = {};
    DDS_SUPPORTED_COUNTRIES.forEach(c => { expanded[c.code] = false; });
    return {
      visible: false,
      showAll: false,
      DDS_SUPPORTED_COUNTRIES,
      expanded,
      collapsedHeight: `${VISIBLE_ROWS * ROW_HEIGHT + (VISIBLE_ROWS - 1) * ROW_GAP}px`,
    };
  },

  mounted() {
    this.visible = localStorage.getItem(LS_KEY) !== 'true';
  },

  methods: {
    toggleCountry(code) {
      this.$set(this.expanded, code, !this.expanded[code]);
    },

    dismiss() {
      localStorage.setItem(LS_KEY, 'true');
      this.visible = false;
    },
  },
};
</script>

<style scoped lang="scss">
.dds-regions-info {
  position: relative;
  background: #e3f2fd;
  border-left: 4px solid var(--v-primary-base);
  border-radius: 8px;
  padding: 16px 20px 12px;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.08);

  .close-btn {
    position: absolute;
    top: -10px;
    right: -10px;
    width: 26px !important;
    height: 26px !important;
    min-width: 26px !important;
  }

  // Grid wrapper clips to 2 rows when collapsed — max-height set via inline :style (Vue 2 compatible)
  .grid-wrapper {
    overflow: hidden;
    transition: max-height 0.3s ease;
  }

  .countries-grid {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(185px, 1fr));
    grid-auto-rows: 34px;
    gap: 4px;
    align-items: start;
  }

  .country-card {
    border: 1px solid rgba(0, 0, 0, 0.07);
    border-radius: 6px;
    background: rgba(255, 255, 255, 0.65);
    height: 34px;
    overflow: visible; // allow v-menu popup to escape the card boundary
  }

  .country-header {
    display: flex;
    align-items: center;
    height: 34px;
    padding: 0 6px;
    cursor: pointer;
    user-select: none;
    overflow: hidden;

    &.no-pointer {
      cursor: default;
    }

    &:not(.no-pointer):hover .country-name {
      text-decoration: underline;
    }
  }

  .country-flag {
    font-size: 15px;
    line-height: 1;
    flex-shrink: 0;
  }

  .country-name {
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    font-size: 11.5px;
    font-weight: 500;
    flex: 1;
    min-width: 0;
  }

  .count-chip {
    font-size: 10px;
    flex-shrink: 0;
    height: 16px !important;
  }

  .expand-icon {
    opacity: 0.65;
    flex-shrink: 0;
  }

  .show-all-btn {
    font-size: 12px !important;
    letter-spacing: 0.3px;
    padding: 0 12px !important;
    border-radius: 20px !important;
    box-shadow: 0 2px 6px rgba(0, 0, 0, 0.18) !important;
  }

  .states-popup {
    .states-chips {
      display: flex;
      flex-wrap: wrap;
    }
  }
}
</style>
